/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button } from '@coursera/coursera-ui';

import CodeBlockV2 from 'bundles/code-evaluator/components/CodeBlockV2';
import Modal from 'bundles/phoenix/components/Modal';
import TextField from 'bundles/widget-simulator/components/tools/TextField';
import type { GraderConfiguration, UserSuppliedObject } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/GraderConfigurationEditorModal';

const styles = {
  alertContainer: css`
    .bt3-alert {
      padding: 15px;
      margin-bottom: 20px;
      border: 1px solid transparent;
      border-radius: 4px;
    }

    .bt3-alert h4 {
      margin-top: 0;
      color: inherit;
    }

    .bt3-alert > p,
    .bt3-alert > ul {
      margin-bottom: 0;
    }

    .bt3-alert > p + p {
      margin-top: 5px;
    }

    .bt3-alert-danger {
      background-color: var(--cds-color-feedback-error-invert-weak);
      border-color: var(--cds-color-red-100);
      color: var(--cds-color-red-800);
    }

    .bt3-alert-danger hr {
      border-top-color: var(--cds-color-red-100);
    }
  `,
};

// NOTE: There is a line with trailing whitespace in this string. It is there intentionally
// so the user's cursor starts tabbed in. Do not remove it.
const DEFAULT_CONFIG_JSON = `{
  
}`;

type PropsFromCaller = {
  name: string;
  title: string;
  config?: GraderConfiguration;
  onClose: () => void;
  onSaveButtonClick: (config: GraderConfiguration) => void;
};

type PropsToComponent = PropsFromCaller;

type State = {
  label: string;
  configJson: string;
  errors?: Array<string>;
};

type ValidationErrorResponse = {
  type: 'error';
  errors: Array<string>;
};

type ValidationSuccessResponse = {
  type: 'success';
  config: GraderConfiguration;
};

type Validation = ValidationErrorResponse | ValidationSuccessResponse;

class GraderConfigurationEditorModal extends React.Component<PropsToComponent, State> {
  constructor(props: PropsToComponent) {
    super(props);
    const { config } = props;

    if (config) {
      this.state = {
        label: config.label,
        configJson: JSON.stringify(config.config, null, 2),
      };
    } else {
      this.state = {
        label: '',
        configJson: DEFAULT_CONFIG_JSON,
      };
    }
  }

  onSaveButtonClick = () => {
    const { onSaveButtonClick } = this.props;

    const response = this.validate();

    if (response.type === 'error') {
      this.setState({ errors: response.errors });
    } else {
      this.setState({ errors: undefined });
      onSaveButtonClick(response.config);
    }
  };

  onNameInputChange = (event: $TSFixMe) => {
    this.setState({ label: event.target.value });
  };

  onEditorChange = (configJson: $TSFixMe) => {
    this.setState({ configJson });
  };

  validate(): Validation {
    const { label, configJson } = this.state;
    const errors: Array<string> = [];
    let config: UserSuppliedObject = {};

    // Make sure the config has a name so we can render something in the dropdown list.
    if (!(label || '').trim()) {
      errors.push(_t('Please enter a name'));
    }

    // Make sure this is valid JSON before allowing it to be saved.
    try {
      config = JSON.parse(configJson);
    } catch (e) {
      errors.push(e.toString());
    }

    if (errors.length > 0) {
      return {
        type: 'error',
        errors,
      };
    }

    // Everything looks good.
    return {
      type: 'success',
      config: {
        id: '', // This ID will not be used.
        label,
        config,
      },
    };
  }

  render() {
    const { name, title, onClose } = this.props;
    const { errors, label, configJson } = this.state;

    return (
      <Modal className="rc-GraderConfigurationEditorModal" modalName={name} handleClose={onClose}>
        <h3 className="modal-title">{title}</h3>

        <div className="modal-content">
          <TextField
            fieldName="GraderConfigurationEditorModalTextField"
            htmlAttributes={{
              type: 'text',
              style: {
                width: '100%',
              },
              value: label,
              placeholder: _t('Name'),
              onChange: this.onNameInputChange,
            }}
          />
          <CodeBlockV2
            readOnly={false}
            codeLanguage="json"
            expression={configJson}
            maxLines={20}
            onChange={this.onEditorChange}
          />
        </div>

        {errors && (
          <div css={styles.alertContainer}>
            <div className="error-message bt3-alert bt3-alert-danger">
              <ul>
                {errors.map((error) => (
                  <li>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <Button label={_t('Save')} type="primary" size="sm" onClick={this.onSaveButtonClick} />
          <Button label={_t('Cancel')} type="secondary" size="sm" onClick={onClose} />
        </div>
      </Modal>
    );
  }
}

export default GraderConfigurationEditorModal;
